import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { storeProfileAPI } from '@/lib/api';
import { supabase } from '@/lib/supabaseClient';
import { exitAppFullscreen } from '@/lib/fullscreenUtils';

const _decodeJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [storeProfile, setStoreProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonation, setIsImpersonation] = useState(() => sessionStorage.getItem('kiranaflow_impersonation') === 'true');

  const fetchStoreProfile = useCallback(async () => {
    try {
      const res = await storeProfileAPI.get();
      setStoreProfile(res.data);
      localStorage.setItem('kiranaflow_store_profile', JSON.stringify(res.data));
    } catch (e) {
      const status = e.response?.status;
      const detail = (e.response?.data?.detail || '').toLowerCase();
      // Clear auth state for hard auth failures: invalid/expired token (401), or
      // account suspended/subscription expired (403). Other 403s (permission denials,
      // "no owner context" for platform_admin) must NOT wipe state.
      const isHardAuthFailure =
        status === 401 ||
        (status === 403 && (detail.includes('suspended') || detail.includes('expired')));
      if (isHardAuthFailure) {
        setUser(null);
        setStoreProfile(null);
        localStorage.removeItem('kiranaflow_store_profile');
        // The API interceptor also does this cleanup, but it skips when on /login.
        // Remove explicitly here so stale items don't survive a page reload.
        localStorage.removeItem('kiranaflow_token');
        localStorage.removeItem('kiranaflow_refresh_token');
        localStorage.removeItem('kiranaflow_user');
        return;
      }
      // Other errors (permission denial, no owner context, profile not yet created) — ignore.
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        // Bootstrap sessionStorage from the URL token before reading auth state.
        // ImpersonateRedirect can't do this first — the loading screen blocks it until after init().
        if (window.location.pathname === '/impersonate') {
          const params = new URLSearchParams(window.location.search);
          const token = params.get('token');
          if (token) {
            try {
              const payload = JSON.parse(
                atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
              );
              sessionStorage.setItem('kiranaflow_impersonation', 'true');
              sessionStorage.setItem('kiranaflow_token', token);
              sessionStorage.setItem('kiranaflow_user', JSON.stringify({
                id:              payload.sub,
                role:            payload.role,
                owner_id:        payload.owner_id,
                store_id:        payload.store_id,
                shop_name:       payload.shop_name  || '',
                owner_name:      payload.owner_name || '',
                mobile:          '',
                membership_tier: payload.membership_tier || 'starter',
              }));
              setIsImpersonation(true);
            } catch { /* malformed token — fall through to login */ }
          }
        }

        // Impersonation tabs keep their auth in sessionStorage (tab-isolated, doesn't affect admin's localStorage)
        const imp = sessionStorage.getItem('kiranaflow_impersonation') === 'true';
        const storedUser  = imp ? sessionStorage.getItem('kiranaflow_user')  : localStorage.getItem('kiranaflow_user');
        const storedToken = imp ? sessionStorage.getItem('kiranaflow_token') : localStorage.getItem('kiranaflow_token');
        if (storedUser && storedToken) {
          const u = JSON.parse(storedUser);
          // Decode JWT to pick up aal and mfa_exempt so ProtectedRoute can guard routes
          const jwtPayload = _decodeJwt(storedToken);
          const aal        = jwtPayload.aal        || 'aal1';
          const mfa_exempt = jwtPayload.mfa_exempt ?? false;
          const enriched   = { ...u, aal, mfa_exempt };
          setUser(enriched);
          // Only fetch store profile when MFA is already satisfied (aal2) or user is exempt.
          // If aal1 + not exempt, ProtectedRoute will redirect to /mfa/verify first.
          const needsMFA = !mfa_exempt && aal !== 'aal2';
          if (u.role !== 'platform_admin' && !needsMFA) {
            await fetchStoreProfile();
          }
        }
      } catch (error) {
        if (sessionStorage.getItem('kiranaflow_impersonation') === 'true') {
          sessionStorage.clear();
        } else {
          localStorage.removeItem('kiranaflow_user');
          localStorage.removeItem('kiranaflow_token');
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchStoreProfile]);

  // Keep the localStorage token copies (read by api.js's axios interceptor) in sync
  // with whatever supabase-js's own internal auto-refresh timer does in the
  // background — otherwise a token silently rotated while the tab was
  // backgrounded leaves api.js holding a stale, already-used refresh token,
  // which only surfaces once a later request 401s.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'TOKEN_REFRESHED' || !session) return;
      // Impersonation tabs run on the admin's own Supabase session (shared via
      // localStorage across all tabs on this origin) — never let this overwrite
      // the impersonated user's ad-hoc JWT, which lives only in sessionStorage
      // and has nothing to do with this event.
      if (sessionStorage.getItem('kiranaflow_impersonation') === 'true') return;
      localStorage.setItem('kiranaflow_token', session.access_token);
      localStorage.setItem('kiranaflow_refresh_token', session.refresh_token);
    });
    return () => subscription?.unsubscribe();
  }, []);

  const login = async (responseData) => {
    const userData = responseData.user || responseData;
    const token    = responseData.token;
    // Normalise legacy 'admin' → 'owner'
    if (userData.role === 'admin') userData.role = 'owner';

    // Decode aal and mfa_exempt from the token (should be aal2 by the time login() is called)
    const jwtPayload = token ? _decodeJwt(token) : {};
    const aal        = jwtPayload.aal        || 'aal1';
    const mfa_exempt = jwtPayload.mfa_exempt ?? false;
    const enriched   = { ...userData, aal, mfa_exempt };

    setUser(enriched);
    if (token) {
      localStorage.setItem('kiranaflow_token', token);
      // Keep Supabase JS client session in sync so mfa.* calls and token refresh work correctly
      const refreshToken = responseData.refresh_token || localStorage.getItem('kiranaflow_refresh_token') || '';
      if (refreshToken) {
        await supabase.auth.setSession({ access_token: token, refresh_token: refreshToken }).catch(() => {});
      }
    }
    if (responseData.refresh_token) localStorage.setItem('kiranaflow_refresh_token', responseData.refresh_token);
    if (responseData.session_id)    localStorage.setItem('kiranaflow_session_id', responseData.session_id);
    localStorage.setItem('kiranaflow_user', JSON.stringify(enriched));
    // Platform admin has no store — skip the store profile fetch entirely.
    if (enriched.role !== 'platform_admin') {
      await fetchStoreProfile();
    }
  };

  const updateAal = (newAal) => {
    setUser(prev => prev ? { ...prev, aal: newAal } : prev);
  };

  const logout = () => {
    exitAppFullscreen();
    qc.clear();
    setUser(null);
    setStoreProfile(null);
    if (sessionStorage.getItem('kiranaflow_impersonation') === 'true') {
      // Impersonation tab — clear sessionStorage only; never touch the admin's localStorage
      sessionStorage.clear();
      window.close();
      // Fallback if browser blocks window.close() (tab not opened via window.open)
      window.location.href = '/login';
    } else {
      localStorage.removeItem('kiranaflow_user');
      localStorage.removeItem('kiranaflow_token');
      localStorage.removeItem('kiranaflow_refresh_token');
      localStorage.removeItem('kiranaflow_store_profile');
      localStorage.removeItem('kiranaflow_session_id');
    }
  };

  const refreshStoreProfile = async () => { await fetchStoreProfile(); };

  const value = {
    user,
    storeProfile,
    isImpersonation,
    refreshStoreProfile,
    login,
    logout,
    updateAal,
    loading,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
